import { useEffect, useState } from "react";
import { Loader2, RefreshCcw, AlertCircle } from "lucide-react";
import { getArcRate } from "@/lib/api";

const POLL_MS = 30_000;

function formatTs(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  } catch {
    return "—";
  }
}

/**
 * Public ARC ecosystem rate ticker. Polls GET /api/arc/rate every 30 s.
 * Renders three states: loading, available, unavailable. NEVER calls
 * the rate "market price".
 */
export default function RateTicker() {
  const [rate, setRate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    let timer = null;
    const tick = async () => {
      try {
        const data = await getArcRate();
        if (!alive) return;
        setRate(data);
        setError(null);
      } catch (e) {
        if (!alive) return;
        setError("network");
      } finally {
        if (alive) setLoading(false);
      }
    };
    tick();
    timer = setInterval(tick, POLL_MS);
    return () => {
      alive = false;
      if (timer) clearInterval(timer);
    };
  }, []);

  const unavailable = !error && (rate?.status === "unavailable" || !rate?.eur_per_arc);

  return (
    <div
      data-testid="redeem-rate-ticker"
      data-rate-status={error ? "error" : unavailable ? "unavailable" : "active"}
      className="glass rounded-md p-5"
    >
      <div className="flex items-center justify-between mb-3">
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">
          Ecosystem rate
        </span>
        <RefreshCcw className="h-3.5 w-3.5 text-white/30" />
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-white/40 text-sm">
          <Loader2 className="h-4 w-4 animate-spin" /> loading…
        </div>
      ) : error ? (
        <div className="flex items-center gap-2 text-[#FF3B30] text-sm" data-testid="redeem-rate-error">
          <AlertCircle className="h-4 w-4" /> ecosystem rate temporarily unavailable
        </div>
      ) : unavailable ? (
        <div data-testid="redeem-rate-unavailable">
          <div className="font-display text-2xl text-white/40">— EUR / ARC</div>
          <p className="mt-2 font-mono text-[11px] text-white/40">
            ecosystem rate temporarily unavailable
          </p>
        </div>
      ) : (
        <div data-testid="redeem-rate-available">
          <div className="font-display text-3xl font-black text-[#39FF14] glow-green">
            <span data-testid="redeem-rate-value">{rate.eur_per_arc}</span>
            <span className="ml-2 text-base text-[#00F0FF]">EUR / ARC</span>
          </div>
          <p className="mt-2 font-mono text-[11px] tracking-wide text-white/40">
            label · {rate.label} · updated {formatTs(rate.last_updated)}
          </p>
        </div>
      )}
    </div>
  );
}
